import { Box, Stack, Text, Title, Group, ActionIcon, Badge, Card } from '@mantine/core'
import { IconArrowLeft, IconMapPin, IconCalendar, IconUser, IconTag } from '@tabler/icons-react'
import type { Spotting } from '../../types/api'
import CategoryDisplay from './CategoryDisplay'

interface SpottingDetailProps {
    spotting: Spotting
    onBack: () => void
}

export function SpottingDetail({ spotting, onBack }: SpottingDetailProps) {
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    return (
        <Box className="h-full">
            <Stack gap="md" className="h-full">
                {/* Header with back button */}
                <Group justify="space-between" align="center">
                    <ActionIcon
                        variant="subtle"
                        size="lg"
                        onClick={onBack}
                        style={{ touchAction: 'manipulation' }}
                    >
                        <IconArrowLeft size={20} />
                    </ActionIcon>
                    <Title order={3} size="h4" className="flex-1 text-center">
                        Spotting Details
                    </Title>
                    <div style={{ width: 36 }} /> {/* Spacer for centering */}
                </Group>

                {/* Spotting content */}
                <Stack gap="lg" className="flex-1">
                    {/* Category Badge - Made prominent */}
                    <Card withBorder padding="md" radius="md">
                        <Group gap="sm" align="center">
                            <IconTag size={20} color="var(--mantine-color-blue-6)" />
                            <Text size="sm" c="dimmed" fw={500}>Category:</Text>
                            <Badge
                                size="lg"
                                variant="filled"
                                color="blue"
                                radius="md"
                                style={{ fontSize: '14px', fontWeight: 600 }}
                            >
                                <CategoryDisplay categoryId={spotting.categoryId} />
                            </Badge>
                        </Group>
                    </Card>

                    {/* Image - Moved up for better visual flow */}
                    {spotting.imageUrl && (
                        <div>
                            <div className="relative">
                                <img
                                    src={spotting.imageUrl}
                                    alt="Spotting image"
                                    className="w-full rounded-lg shadow-md border border-gray-200 dark:border-gray-600"
                                    style={{
                                        maxHeight: '300px',
                                        objectFit: 'cover',
                                        touchAction: 'manipulation'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Description */}
                    {spotting.description && (
                        <Card withBorder padding="md" radius="md">
                            <Text fw={600} size="sm" mb="xs" c="blue">Description</Text>
                            <Text size="sm" className="whitespace-pre-wrap">
                                {spotting.description}
                            </Text>
                        </Card>
                    )}

                    {/* Info Cards */}
                    <Stack gap="sm">
                        {/* Creator */}
                        <Card withBorder padding="sm" radius="md">
                            <Group gap="xs" align="center">
                                <IconUser size={16} color="var(--mantine-color-green-6)" />
                                <Text fw={500} size="sm" c="dimmed">Created by:</Text>
                                <Text size="sm" fw={600}>
                                    {spotting.creator.username}
                                </Text>
                            </Group>
                        </Card>

                        {/* Location */}
                        <Card withBorder padding="sm" radius="md">
                            <Group gap="xs" align="center">
                                <IconMapPin size={16} color="var(--mantine-color-red-6)" />
                                <Text fw={500} size="sm" c="dimmed">Location:</Text>
                                <Text size="sm" c="blue" fw={500} className="flex-1">
                                    {spotting.address || `${spotting.latitude.toFixed(6)}, ${spotting.longitude.toFixed(6)}`}
                                </Text>
                            </Group>
                        </Card>

                        {/* Date */}
                        <Card withBorder padding="sm" radius="md">
                            <Group gap="xs" align="center">
                                <IconCalendar size={16} color="var(--mantine-color-orange-6)" />
                                <Text fw={500} size="sm" c="dimmed">Spotted on:</Text>
                                <Text size="sm" fw={500}>
                                    {formatDate(spotting.createdAt)}
                                </Text>
                            </Group>
                        </Card>
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    )
} 